import React from 'react';
import { X, Copy, Check, ExternalLink, User } from 'lucide-react';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC',
];

/**
 * Information sub-tab: personal info, booking & calendar, professional details and socials.
 * @param {Object} props
 * @param {Object} props.form - Digital Business Card form state.
 * @param {(field: string, val: string) => void} props.handleTextChange
 * @returns {JSX.Element}
 */
export default function InformationSection({ form, setForm, handleTextChange, fileInputRef, handlePhotoFile, copiedLink, copyBookingLink, fullBookingUrl, agent }) {
  return (
    <>
      {/* Section 1: Personal info */}
      <div className="panel p-5 space-y-4">
        <h3 className="text-sm font-semibold text-white/90 uppercase tracking-wider">
          Personal info
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 items-start">
          {/* Avatar Upload Dropzone */}
          <div className="md:col-span-5 flex flex-col items-center text-center p-4 border border-dashed border-white/20 rounded-xl bg-white/[0.02]">
            <div className="relative mb-3">
              {form.photoUrl ? (
                <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-civix-purple/60 shadow-md">
                  <img
                    src={form.photoUrl}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="w-20 h-20 rounded-full bg-white/5 border border-white/10 flex flex-col items-center justify-center text-white/40">
                  <User className="w-8 h-8 mb-1" />
                  <span className="text-[10px] font-medium uppercase tracking-wider">Photo</span>
                </div>
              )}

              {form.photoUrl && (
                <button
                  type="button"
                  onClick={() => setForm((prev) => ({ ...prev, photoUrl: '' }))}
                  className="absolute -top-1 -right-1 p-1 bg-red-500 hover:bg-red-600 rounded-full text-white shadow-sm transition"
                  title="Remove photo"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handlePhotoFile(e.target.files[0])}
            />

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full py-2 px-3 border border-civix-purple text-civix-purple hover:bg-civix-purple/10 rounded-lg text-xs font-bold uppercase tracking-wider transition mb-1"
            >
              UPLOAD IMAGE (MAX 300KB)
            </button>
            <span className="text-[11px] text-white/40">or drag and drop file</span>
          </div>

          {/* Right Personal Fields */}
          <div className="md:col-span-7 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-white/70 mb-1">
                  First name *
                </label>
                <input
                  type="text"
                  value={form.firstName}
                  onChange={(e) => handleTextChange('firstName', e.target.value)}
                  placeholder="John"
                  className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-civix-purple"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-white/70 mb-1">
                  Last name *
                </label>
                <input
                  type="text"
                  value={form.lastName}
                  onChange={(e) => handleTextChange('lastName', e.target.value)}
                  placeholder="Doe"
                  className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-civix-purple"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-white/70 mb-1">
                Cell phone
              </label>
              <input
                type="text"
                value={form.cellPhone}
                onChange={(e) => handleTextChange('cellPhone', e.target.value)}
                placeholder="(555) 000-0000"
                className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-civix-purple"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-white/70 mb-1">
                Notes
              </label>
              <input
                type="text"
                value={form.notes}
                onChange={(e) => handleTextChange('notes', e.target.value)}
                placeholder="Licensed life & annuity specialist"
                className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-civix-purple"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-white/70 mb-1">
                  License / Producer Info
                </label>
                <input
                  type="text"
                  value={form.licenseInfo}
                  onChange={(e) => handleTextChange('licenseInfo', e.target.value)}
                  placeholder="LIC #123456"
                  className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-civix-purple"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-white/70 mb-1">
                  National ID
                </label>
                <input
                  type="text"
                  value={form.nationalId}
                  onChange={(e) => handleTextChange('nationalId', e.target.value)}
                  placeholder="NPN #9876543"
                  className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-civix-purple"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section 2: Booking & Calendar */}
      <div className="panel p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white/90 uppercase tracking-wider">
            Booking & Calendar
          </h3>
          <a
            href={fullBookingUrl}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-civix-purple hover:underline flex items-center gap-1"
          >
            Open Live Link <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        <div>
          <label className="block text-xs font-medium text-white/70 mb-1">
            Booking Link (Self-Scheduling)
          </label>
          <div className="flex items-center rounded-lg border border-white/10 bg-black/30 overflow-hidden focus-within:border-civix-purple">
            <span className="px-3 text-xs text-white/40 bg-white/5 border-r border-white/10 py-2.5 whitespace-nowrap">
              {origin}/schedule/
            </span>
            <input
              type="text"
              value={form.bookingSlug}
              onChange={(e) => handleTextChange('bookingSlug', e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, '-'))}
              placeholder="john-doe"
              className="w-full bg-transparent px-3 py-2 text-sm text-white focus:outline-none font-mono"
            />
            <button
              type="button"
              onClick={copyBookingLink}
              className="px-3 py-2 text-white/60 hover:text-white transition hover:bg-white/5"
              title="Copy full link"
            >
              {copiedLink ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
          <p className="text-[11px] text-white/40 mt-1">
            Your direct self-scheduling link for leads in SMS drips and automation templates.
          </p>
        </div>

        {/* 72-Hour Scheduling Cap Checkbox */}
        <div className="p-3.5 rounded-lg border border-purple-500/20 bg-purple-500/5 flex items-start gap-3">
          <input
            id="cap72Hours"
            type="checkbox"
            checked={form.cap72Hours}
            onChange={(e) => handleTextChange('cap72Hours', e.target.checked)}
            className="mt-1 w-4 h-4 rounded text-civix-purple border-white/20 focus:ring-civix-purple focus:ring-offset-0 bg-black/40"
          />
          <div className="flex-1">
            <label htmlFor="cap72Hours" className="text-sm font-medium text-white cursor-pointer select-none">
              Enable 72-Hour Scheduling Cap
            </label>
            <p className="text-xs text-white/60 mt-0.5">
              Limit client appointments to a maximum of 72 hours out (prevents ghost/stale bookings).
            </p>
          </div>
        </div>
      </div>

      {/* Section 3: Work details */}
      <div className="panel p-5 space-y-4">
        <h3 className="text-sm font-semibold text-white/90 uppercase tracking-wider">
          Work details
        </h3>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-white/70 mb-1">Title</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => handleTextChange('title', e.target.value)}
              placeholder="Senior Managing Underwriter"
              className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-civix-purple"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-white/70 mb-1">Organization</label>
            <input
              type="text"
              value={form.organization}
              onChange={(e) => handleTextChange('organization', e.target.value)}
              placeholder="CIVIX Insurance Group"
              className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-civix-purple"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-white/70 mb-1">Work URL</label>
              <input
                type="text"
                value={form.workUrl}
                onChange={(e) => handleTextChange('workUrl', e.target.value)}
                placeholder="https://civixcrm.ai"
                className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-civix-purple"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-white/70 mb-1">Work email</label>
              <input
                type="email"
                value={form.workEmail}
                onChange={(e) => handleTextChange('workEmail', e.target.value)}
                placeholder="agent@civixcrm.ai"
                className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-civix-purple"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-white/70 mb-1">Work address label</label>
            <input
              type="text"
              value={form.addressLabel}
              onChange={(e) => handleTextChange('addressLabel', e.target.value)}
              placeholder="Corporate HQ / Office"
              className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-civix-purple"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-white/70 mb-1">Work address street</label>
              <input
                type="text"
                value={form.street}
                onChange={(e) => handleTextChange('street', e.target.value)}
                placeholder="100 Main St, Suite 400"
                className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-civix-purple"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-white/70 mb-1">Work address city</label>
              <input
                type="text"
                value={form.city}
                onChange={(e) => handleTextChange('city', e.target.value)}
                placeholder="Denver"
                className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-civix-purple"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-white/70 mb-1">Work address state</label>
              <select
                value={form.state}
                onChange={(e) => handleTextChange('state', e.target.value)}
                className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-civix-purple"
              >
                {US_STATES.map((st) => (
                  <option key={st} value={st} className="bg-gray-900 text-white">
                    {st}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-white/70 mb-1">Work address ZIP code</label>
              <input
                type="text"
                value={form.zip}
                onChange={(e) => handleTextChange('zip', e.target.value)}
                placeholder="80202"
                className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-civix-purple"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Section 4: Social URLs */}
      <div className="panel p-5 space-y-4">
        <h3 className="text-sm font-semibold text-white/90 uppercase tracking-wider">
          Social URLs
        </h3>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-white/70 mb-1">Facebook URL</label>
            <input
              type="text"
              value={form.facebook}
              onChange={(e) => handleTextChange('facebook', e.target.value)}
              placeholder="https://facebook.com/username"
              className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-civix-purple"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-white/70 mb-1">LinkedIn URL</label>
            <input
              type="text"
              value={form.linkedin}
              onChange={(e) => handleTextChange('linkedin', e.target.value)}
              placeholder="https://linkedin.com/in/username"
              className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-civix-purple"
            />
          </div>
        </div>
      </div>
    </>
  );
}
